import { useEffect, useRef } from 'react';
import { useUserControlsContext } from '../contexts/UserControlsContext';
import { useSegmentTrack } from './useSegmentTrack';

// Top-level views, tracked when the view is actually shown on screen. Distinct from
// 'Account Viewed' (useTrackAccountViewed), which is click-driven and carries accountId.
export type ViewEventName =
  | 'Categories Viewed'
  | 'Skills Viewed'
  | 'Personas Viewed'
  | 'Prompts Viewed'
  | 'My Actions Viewed'
  | 'Signals Viewed';

/**
 * Fires a Segment view event when a top-level view is shown — on mount, and again
 * whenever `event` changes (e.g. switching Library tabs). Captures the view regardless
 * of how the user arrived (nav rail, direct URL, redirect).
 *
 * - Waits for `userId` so events aren't attributed to a null user (the provider renders
 *   children before /api/me resolves), matching the click-driven 'Account Viewed'.
 * - Guards on the last-fired event so identity loading or SSE re-renders don't
 *   double-count; a real change (tab switch) still fires.
 * - `properties` (e.g. `{ surface: 'sidenav' }` for 'Signals Viewed') is read through a
 *   ref — it doesn't affect whether we fire (that's `event` + `userId`), only the
 *   payload, and an inline object literal at the call site would otherwise re-run the
 *   effect every render. The ref keeps the payload current without widening the deps.
 */
export function useTrackViewViewed(event: ViewEventName, properties?: Record<string, unknown>): void {
  const track = useSegmentTrack();
  const { userId } = useUserControlsContext();
  const lastFired = useRef<ViewEventName | null>(null);
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  useEffect(() => {
    if (userId == null) return;
    if (lastFired.current === event) return;
    lastFired.current = event;
    track(event, propertiesRef.current);
  }, [event, userId, track]);
}
